import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Grid,
} from "@mui/material";
import { Add, VpnKey, FileCopy, Visibility, VisibilityOff, Delete, Save, Edit, MoreVert, Group, Search } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { passwordService } from "../services/password.service";
import { groupService } from "../services/group.service";
import { StoredPassword } from "../types/password.types";
import { PasswordGroup } from "../types/group.types";

const Dashboard: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState<StoredPassword[]>([]);
  const [groups, setGroups] = useState<PasswordGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [editGroup, setEditGroup] = useState<PasswordGroup | null>(null);
  const [editPassword, setEditPassword] = useState<StoredPassword | null>(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [openGroupList, setOpenGroupList] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderBy, setOrderBy] = useState<keyof StoredPassword | "">("siteName");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, selectedGroup]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [passwordsData, groupsData] = await Promise.all([passwordService.getAll(selectedGroup), groupService.getAll()]);
      setPasswords(passwordsData);
      setGroups(groupsData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = async (passwordId: string) => {
    try {
      const { password } = await passwordService.getDecryptedPassword(passwordId);
      await navigator.clipboard.writeText(password);
      alert("Senha copiada para a área de transferência!");
    } catch (error) {
      alert("Erro ao copiar senha");
    }
  };

  const handleShowPassword = async (passwordId: string) => {
    try {
      const { password } = await passwordService.getDecryptedPassword(passwordId);
      alert(`Senha: ${password}`);
    } catch (error) {
      alert("Erro ao obter senha");
    }
  };

  const handleDeletePassword = async (passwordId: string) => {
    if (window.confirm("Tem certeza que deseja excluir esta senha?")) {
      try {
        await passwordService.delete(passwordId);
        loadData();
        alert("Senha excluída com sucesso!");
      } catch (error) {
        alert("Erro ao excluir senha");
      }
    }
  };

  const handleEditPassword = async (passwordId: string) => {
    try {
      const passwordToEdit = await passwordService.getById(passwordId);
      const decryptedPassword = await passwordService.getDecryptedPassword(passwordId);
      setEditPassword({ ...passwordToEdit, password: decryptedPassword.password });
    } catch (error) {
      alert("Erro ao carregar senha para edição.");
    }
  };

  const handleUpdatePassword = async () => {
    if (!editPassword) return;
    try {
      await passwordService.update(editPassword.id, {
        siteName: editPassword.siteName,
        username: editPassword.username,
        password: editPassword.password,
        url: editPassword.url,
        notes: editPassword.notes,
        groupId: editPassword.group.id,
      });
      setEditPassword(null);
      loadData();
      alert("Senha atualizada com sucesso!");
    } catch (error) {
      alert("Erro ao atualizar senha.");
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (window.confirm("Tem certeza que deseja excluir este grupo? Todas as senhas associadas serão excluídas.")) {
      try {
        await groupService.delete(groupId);
        loadData();
        alert("Grupo excluído com sucesso!");
      } catch (error) {
        alert("Erro ao excluir grupo.");
      }
    }
  };

  const handleEditGroup = (group: PasswordGroup) => {
    setEditGroup(group);
  };

  const handleUpdateGroup = async () => {
    if (!editGroup) return;
    try {
      await groupService.update(editGroup._id, {
        name: editGroup.name,
        description: editGroup.description,
      });
      setEditGroup(null);
      loadData();
      alert("Grupo atualizado com sucesso!");
    } catch (error) {
      alert("Erro ao atualizar grupo.");
    }
  };

  const handleSort = (column: keyof StoredPassword) => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(column);
      setOrderDirection("asc");
    }
  };

  const getSortedPasswords = () => {
    const sorted = [...passwords].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (orderBy === "group") {
        aValue = a.group?.name || "";
        bValue = b.group?.name || "";
      } else if (orderBy) {
        aValue = a[orderBy] as string;
        bValue = b[orderBy] as string;
      }

      if (typeof aValue === "string") {
        return orderDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return 0;
    });

    return sorted;
  };

  const getFilteredPasswords = () => {
    const sortedPasswords = getSortedPasswords();
    if (!searchQuery) {
      return sortedPasswords;
    }
    const lowerCaseQuery = searchQuery.toLowerCase();
    return sortedPasswords.filter(
      (password) =>
        password.siteName.toLowerCase().includes(lowerCaseQuery) ||
        password.notes?.toLowerCase().includes(lowerCaseQuery) ||
        password.group?.name.toLowerCase().includes(lowerCaseQuery)
    );
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: "center" }}>
        <Alert severity="warning">Você precisa estar logado para acessar o dashboard.</Alert>
        <Button variant="contained" component={Link} to="/login" sx={{ mt: 2 }}>
          Fazer Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          gap: { xs: 2, md: 0 },
        }}
      >
        <Typography variant="h4" component="h1" color="primary">
          Minhas Senhas
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "center", md: "flex-end" } }}>
          <Button variant="outlined" component={Link} to="/create-group" startIcon={<Add />}>
            Criar Grupo
          </Button>
          <Button variant="outlined" onClick={() => setOpenGroupList(true)} startIcon={<Group />}>
            Ver Grupos
          </Button>
          <Button variant="contained" component={Link} to="/generator" startIcon={<VpnKey />}>
            Gerar Senha
          </Button>
          <Button variant="contained" color="secondary" component={Link} to="/save-password" startIcon={<Save />}>
            Salvar Senha
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 3,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <FormControl sx={{ flexGrow: 1, mr: { sm: 3 } }}>
          <InputLabel id="group-filter-label">Filtrar por Grupo</InputLabel>
          <Select
            labelId="group-filter-label"
            id="group-filter"
            value={selectedGroup}
            label="Filtrar por Grupo"
            onChange={(e) => setSelectedGroup(e.target.value as string)}
          >
            <MenuItem value="">Todos os Grupos</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group._id} value={group._id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          sx={{ flexGrow: 3 }}
          label="Pesquisar Senhas"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell onClick={() => handleSort("siteName")} sx={{ cursor: "pointer" }}>
                Site/Aplicação {orderBy === "siteName" ? (orderDirection === "asc" ? "↑" : "↓") : ""}
              </TableCell>
              <TableCell onClick={() => handleSort("group")} sx={{ cursor: "pointer" }}>
                Grupo {orderBy === "group" ? (orderDirection === "asc" ? "↑" : "↓") : ""}
              </TableCell>
              <TableCell onClick={() => handleSort("username")} sx={{ cursor: "pointer" }}>
                Usuário {orderBy === "username" ? (orderDirection === "asc" ? "↑" : "↓") : ""}
              </TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredPasswords().length > 0 ? (
              getFilteredPasswords().map((password) => (
                <TableRow key={password.id} hover>
                  <TableCell component="th" scope="row">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {password.siteName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {password.group && <Chip label={password.group.name} color="primary" variant="outlined" size="small" />}
                  </TableCell>
                  <TableCell>{password.username}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleCopyPassword(password.id)} title="Copiar senha">
                      <FileCopy />
                    </IconButton>
                    <IconButton color="secondary" onClick={() => handleShowPassword(password.id)} title="Visualizar senha">
                      <Visibility />
                    </IconButton>
                    <IconButton color="warning" onClick={() => handleEditPassword(password.id)} title="Editar senha">
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeletePassword(password.id)} title="Excluir senha">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body1" color="text.secondary">
                    {selectedGroup ? "Nenhuma senha encontrada neste grupo." : "Nenhuma senha cadastrada."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editPassword} onClose={() => setEditPassword(null)} fullWidth>
        <DialogTitle>Editar Senha</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Site/Aplicação"
            type="text"
            fullWidth
            variant="standard"
            value={editPassword?.siteName || ""}
            onChange={(e) => setEditPassword({ ...editPassword!, siteName: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Usuário"
            type="text"
            fullWidth
            variant="standard"
            value={editPassword?.username || ""}
            onChange={(e) => setEditPassword({ ...editPassword!, username: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Senha"
            type={showEditPassword ? "text" : "password"}
            fullWidth
            variant="standard"
            value={editPassword?.password || ""}
            onChange={(e) => setEditPassword({ ...editPassword!, password: e.target.value })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={() => setShowEditPassword(!showEditPassword)} edge="end">
                    {showEditPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Adição do Select para alteração de grupo */}
          <FormControl fullWidth margin="dense">
            <InputLabel id="edit-group-select-label">Grupo</InputLabel>
            <Select
              labelId="edit-group-select-label"
              value={editPassword?.group.id || ""}
              label="Grupo"
              onChange={(e) => {
                const newGroupId = e.target.value as string;
                setEditPassword({
                  ...editPassword!,
                  group: { id: newGroupId, name: groups.find((g) => g._id === newGroupId)?.name || "" },
                });
              }}
            >
              {groups.map((group) => (
                <MenuItem key={group._id} value={group._id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="URL"
            type="text"
            fullWidth
            variant="standard"
            value={editPassword?.url || ""}
            onChange={(e) => setEditPassword({ ...editPassword!, url: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Observações"
            type="text"
            fullWidth
            variant="standard"
            multiline
            rows={3}
            value={editPassword?.notes || ""}
            onChange={(e) => setEditPassword({ ...editPassword!, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPassword(null)}>Cancelar</Button>
          <Button onClick={handleUpdatePassword}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Lista de Grupos e Edição de Grupo */}
      <Dialog open={openGroupList} onClose={() => setOpenGroupList(false)} fullWidth maxWidth="sm">
        <DialogTitle>Meus Grupos</DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Grupo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length > 0 ? (
                  groups.map((group) => (
                    <TableRow key={group._id} hover>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.description}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleEditGroup(group)}>
                          <Edit />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteGroup(group._id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Nenhum grupo cadastrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGroupList(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Edição de Grupo (aberto pelo diálogo da lista de grupos) */}
      <Dialog open={!!editGroup} onClose={() => setEditGroup(null)}>
        <DialogTitle>Editar Grupo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Grupo"
            type="text"
            fullWidth
            variant="standard"
            value={editGroup?.name || ""}
            onChange={(e) => setEditGroup({ ...editGroup!, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Descrição"
            type="text"
            fullWidth
            variant="standard"
            multiline
            rows={4}
            value={editGroup?.description || ""}
            onChange={(e) => setEditGroup({ ...editGroup!, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroup(null)}>Cancelar</Button>
          <Button onClick={handleUpdateGroup}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Dashboard;
